import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * AssignContactModal - Assign contact as supplier/customer
 * Uses gold theme from BaseModal system
 */
import { useState } from 'react';
import styled from 'styled-components';
import BaseModal, { Input as BaseInput, FormGroup as BaseFormGroup, FormLabel, GhostButton, } from './shared/BaseModal';
import { apiClient } from '../utils/apiClient';
import { notify } from '../utils/notifications';
import { CONTACT_DESIGNATION_LABELS } from '../types/contact';
import { FaUser, FaTruck, FaCheck, FaTimes } from 'react-icons/fa';
const InfoBox = styled.div `
  background: var(--bg-tertiary);
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  border: 1px solid var(--border-color);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--accent-gold), var(--accent-gold-light));
  }
`;
const InfoRow = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;

  &:not(:last-child) {
    border-bottom: 1px solid var(--border-color);
  }
`;
const InfoLabel = styled.span `
  font-size: 14px;
  color: var(--text-tertiary);
`;
const InfoValue = styled.span `
  font-size: 14px;
  color: var(--text-primary);
  font-weight: 600;
`;
const DesignationGrid = styled.div `
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;
const DesignationCard = styled.div `
  border: 2px solid ${props => props.isActive ? props.color : 'var(--border-color)'};
  border-radius: 8px;
  padding: 20px;
  background: ${props => props.isActive ? `${props.color}10` : 'var(--bg-primary)'};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.color};
  }
`;
const DesignationHeader = styled.div `
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
`;
const DesignationIcon = styled.div `
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${props => props.color};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
`;
const DesignationTitle = styled.h3 `
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
`;
const DesignationStatus = styled.div `
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${props => props.isActive ? '#15803D' : 'var(--text-tertiary)'};
  margin-bottom: 14px;
`;
const ActionButton = styled.button `
  width: 100%;
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${props => props.variant === 'add' ? 'var(--accent-gold)' : '#DC2626'};
  background: ${props => props.variant === 'add'
    ? 'linear-gradient(135deg, var(--accent-gold) 0%, var(--accent-gold-dark) 100%)'
    : '#DC2626'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${props => props.variant === 'add'
    ? 'rgba(184, 134, 11, 0.3)'
    : 'rgba(220, 38, 38, 0.3)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const FormGroup = styled(BaseFormGroup) `
  margin-bottom: 12px;
`;
const Input = styled(BaseInput) `
  padding: 12px 14px;
`;
const Select = styled.select `
  width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 14px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.15s ease;

  &:focus {
    outline: none;
    border-color: var(--accent-gold);
    box-shadow: 0 0 0 3px var(--accent-gold-bg);
  }
`;
const SupplierFields = styled.div `
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--border-color);
`;
const ButtonGroup = styled.div `
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--border-color);
`;
const AssignContactModal = ({ contact, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [supplierCode, setSupplierCode] = useState('');
    const [supplierType, setSupplierType] = useState('other');
    const [paymentTerms, setPaymentTerms] = useState('Net 30');
    const isSupplier = contact.designation === 'supplier' || contact.designation === 'both';
    const isCustomer = contact.designation === 'customer' || contact.designation === 'both';
    const handleAction = async (action) => {
        setLoading(true);
        try {
            const body = { action };
            // Add supplier-specific data
            if (action === 'add-supplier') {
                body.supplierData = {
                    supplierCode: supplierCode || undefined,
                    supplierType,
                    paymentTerms
                };
            }
            const response = await apiClient.post(`contacts-assign/${contact.id}`, body);
            if (response.ok) {
                const actionText = action.startsWith('add') ? 'assigned' : 'removed';
                const typeText = action.includes('supplier') ? 'supplier' : 'customer';
                notify.success('Success', `Contact ${actionText} as ${typeText}`);
                onClose(true);
            }
            else {
                const errorData = response.data;
                notify.error('Error', errorData?.error || 'Failed to update assignment');
            }
        }
        catch (error) {
            console.error('Failed to update assignment:', error);
            notify.error('Error', 'Failed to update assignment');
        }
        finally {
            setLoading(false);
        }
    };
    return (_jsxs(BaseModal, { isOpen: true, onClose: () => onClose(false), title: "Assign Contact", titleAccent: "Contact", size: "md", children: [_jsxs(InfoBox, { children: [_jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Company" }), _jsx(InfoValue, { children: contact.companyName })] }), _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Contact Person" }), _jsx(InfoValue, { children: contact.contactPerson })] }), _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Current Designation" }), _jsx(InfoValue, { style: { color: 'var(--accent-gold)' }, children: CONTACT_DESIGNATION_LABELS[contact.designation] })] })] }), _jsxs(DesignationGrid, { children: [_jsxs(DesignationCard, { isActive: isSupplier, color: "var(--accent-gold)", children: [_jsxs(DesignationHeader, { children: [_jsx(DesignationIcon, { color: "var(--accent-gold)", children: _jsx(FaTruck, {}) }), _jsx(DesignationTitle, { children: "Supplier" })] }), _jsxs(DesignationStatus, { isActive: isSupplier, children: [isSupplier ? _jsx(FaCheck, {}) : _jsx(FaTimes, {}), isSupplier ? 'Currently assigned as supplier' : 'Not assigned as supplier'] }), !isSupplier && (_jsxs(SupplierFields, { children: [_jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Supplier Code (optional)" }), _jsx(Input, { value: supplierCode, onChange: (e) => setSupplierCode(e.target.value), placeholder: "Auto-generated if empty" })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Supplier Type" }), _jsxs(Select, { value: supplierType, onChange: (e) => setSupplierType(e.target.value), children: [_jsx("option", { value: "manufacturer", children: "Manufacturer" }), _jsx("option", { value: "wholesaler", children: "Wholesaler" }), _jsx("option", { value: "broker", children: "Broker" }), _jsx("option", { value: "other", children: "Other" })] })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Payment Terms" }), _jsxs(Select, { value: paymentTerms, onChange: (e) => setPaymentTerms(e.target.value), children: [_jsx("option", { value: "Net 30", children: "Net 30" }), _jsx("option", { value: "Net 60", children: "Net 60" }), _jsx("option", { value: "COD", children: "COD" }), _jsx("option", { value: "Due on Receipt", children: "Due on Receipt" })] })] })] })), _jsx(ActionButton, { variant: isSupplier ? 'remove' : 'add', onClick: () => handleAction(isSupplier ? 'remove-supplier' : 'add-supplier'), disabled: loading, children: isSupplier ? (_jsxs(_Fragment, { children: [_jsx(FaTimes, {}), " Remove as Supplier"] })) : (_jsxs(_Fragment, { children: [_jsx(FaCheck, {}), " Add as Supplier"] })) })] }), _jsxs(DesignationCard, { isActive: isCustomer, color: "#15803D", children: [_jsxs(DesignationHeader, { children: [_jsx(DesignationIcon, { color: "#15803D", children: _jsx(FaUser, {}) }), _jsx(DesignationTitle, { children: "Customer" })] }), _jsxs(DesignationStatus, { isActive: isCustomer, children: [isCustomer ? _jsx(FaCheck, {}) : _jsx(FaTimes, {}), isCustomer ? 'Currently assigned as customer' : 'Not assigned as customer'] }), _jsx("div", { style: { flex: 1 } }), _jsx(ActionButton, { variant: isCustomer ? 'remove' : 'add', onClick: () => handleAction(isCustomer ? 'remove-customer' : 'add-customer'), disabled: loading, style: { marginTop: 'auto' }, children: isCustomer ? (_jsxs(_Fragment, { children: [_jsx(FaTimes, {}), " Remove as Customer"] })) : (_jsxs(_Fragment, { children: [_jsx(FaCheck, {}), " Add as Customer"] })) })] })] }), _jsx(ButtonGroup, { children: _jsx(GhostButton, { onClick: () => onClose(false), disabled: loading, children: "Close" }) })] }));
};
export default AssignContactModal;
